// components/AnalysisChart.jsx
import React from "react";
import {
  ResponsiveContainer,
  ComposedChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  ReferenceLine,
  CartesianGrid,
} from "recharts";
import "./chart.css";

function formatDate(ts) {
  return new Date(ts).toLocaleDateString();
}

// --- helpers ---
const toNum = (v) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
};

function formatPriceDynamic(num) {
  const n = toNum(num);
  if (n === null) return "N/A";
  const abs = Math.abs(n);
  if (abs >= 1) return n.toFixed(2);
  if (abs >= 0.01) return n.toFixed(4);
  if (abs >= 0.0001) return n.toFixed(6);
  return n.toFixed(10).replace(/\.?0+$/, "");
}

export default function AnalysisChart({ candles = [], analysis }) {
  if (!candles.length) return <div>No chart data</div>;

  const chartData = candles.map((c) => ({
    time: formatDate(c.openTime),
    open: toNum(c.open),
    high: toNum(c.high),
    low: toNum(c.low),
    close: toNum(c.close),
    volume: toNum(c.volume),
    rsi: toNum(c.rsi),
  }));

  const { longPosition, shortPosition } = analysis || {};
  const entry = toNum(longPosition?.entry ?? shortPosition?.entry);
  const tp    = toNum(longPosition?.tp    ?? shortPosition?.tp);
  const sl    = toNum(longPosition?.sl    ?? shortPosition?.sl);

  const latestRSI = chartData.at(-1)?.rsi ?? 50;

  const CustomTooltip = ({ active, payload, label }) => {
    if (!active || !payload || !payload.length) return null;
    const d = payload[0].payload;
    return (
      <div className="custom-tooltip">
        <div className="tooltip-title">{label}</div>
        <div>🟢 Open: <span>${formatPriceDynamic(d.open)}</span></div>
        <div>🔵 Close: <span>${formatPriceDynamic(d.close)}</span></div>
        <div>🔺 High: <span>${formatPriceDynamic(d.high)}</span></div>
        <div>🔻 Low: <span>${formatPriceDynamic(d.low)}</span></div>
        <div>📦 Volume: <span>{d.volume != null ? d.volume.toLocaleString() : "N/A"}</span></div>
        <div>📉 RSI: <span>{d.rsi != null ? d.rsi.toFixed(2) : "N/A"}</span></div>
      </div>
    );
  };

  const RSITooltip = ({ active, payload, label }) => {
    if (!active || !payload?.length) return null;
    const rsi = payload[0].payload?.rsi;
    return (
      <div className="custom-tooltip">
        <div className="tooltip-title">{label}</div>
        <div>📉 RSI: <span>{rsi != null ? rsi.toFixed(2) : "N/A"}</span></div>
      </div>
    );
  };

  const rsiTickFormatter = (val) => {
    const n = toNum(val);
    if (n === null) return "";
    if (n === 0 || n === 100) return n;
    if (Math.abs(n - latestRSI) < 0.5) return Number(latestRSI).toFixed(2);
    return "";
  };

  const rsiTicks = [0, 30, 50, 70, 100];
  if (!rsiTicks.includes(Math.round(latestRSI))) {
    rsiTicks.splice(2, 1, parseFloat(Number(latestRSI).toFixed(2)));
  }

  return (
    <div className="chart-container">
      <div className="chart-title">📊 Price Chart (Daily)</div>
      <ResponsiveContainer
        width="100%"
        height={typeof window !== "undefined" && window.innerWidth < 480 ? 180 : 220}
      >
        <ComposedChart data={chartData}>
          <CartesianGrid stroke="#333" strokeDasharray="3 3" />
          <XAxis
            dataKey="time"
            tickFormatter={(value) => {
              const date = new Date(value);
              return `${date.getMonth() + 1}/${date.getDate()}`;
            }}
          />
          <YAxis
            domain={["auto", "auto"]}
            orientation="right"
            width={28}
            tickFormatter={(v) => {
              const n = toNum(v);
              return n === null ? "" : `$${formatPriceDynamic(n)}`;
            }}
          />
          <Tooltip content={<CustomTooltip />} />
          <Line type="monotone" dataKey="close" stroke="#00bcd4" dot={false} />

          {entry !== null && (
            <ReferenceLine y={entry} stroke="#4caf50" strokeDasharray="3 3" strokeWidth={2} />
          )}

          {tp !== null && (
            <ReferenceLine
              y={tp}
              stroke="#2196f3"
              strokeDasharray="3 3"
              strokeWidth={2}
              label={{
                value: `TP $${formatPriceDynamic(tp)}`,
                position: "insideTop",
                style: {
                  fill: "#fff",
                  fontWeight: "bold",
                  fontSize: 14,
                  padding: "3px",
                  borderRadius: "4px",
                },
              }}
            />
          )}

          {sl !== null && (
            <ReferenceLine
              y={sl}
              stroke="#f44336"
              strokeDasharray="3 3"
              strokeWidth={2}
              label={{
                value: `SL $${formatPriceDynamic(sl)}`,
                position: "insideTop",
                style: {
                  fill: "#fff",
                  fontWeight: "bold",
                  fontSize: 14,
                  padding: "3px",
                  borderRadius: "4px",
                },
              }}
            />
          )}
        </ComposedChart>
      </ResponsiveContainer>

      <div className="chart-title" style={{ marginTop: "1.5rem" }}></div>
      <ResponsiveContainer width="100%" height={100}>
        <ComposedChart data={chartData}>
          <XAxis
            dataKey="time"
            tickFormatter={(value) => {
              const date = new Date(value);
              return `${date.getMonth() + 1}/${date.getDate()}`;
            }}
          />
          <YAxis
            domain={[0, 100]}
            orientation="right"
            width={30}
            ticks={rsiTicks}
            tickFormatter={rsiTickFormatter}
          />
          <Tooltip content={<RSITooltip />} />
          <Line type="monotone" dataKey="rsi" stroke="#ff9800" dot={false} />
          <ReferenceLine y={70} stroke="#e91e63" strokeDasharray="3 3" />
          <ReferenceLine y={30} stroke="#3f51b5" strokeDasharray="3 3" />
        </ComposedChart>
      </ResponsiveContainer>
    </div>
  );
}
